import React, {
  FC,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Typography,
  CircularProgress,
  Alert,
  Box,
  Paper,
  Grid,
  IconButton,
  TableSortLabel,
  Button,
  Chip,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import useFileUpload, { BatchProgress, UploadFileItem, UploadPhase } from 'src/hooks/useFileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import InsightsIcon from '@mui/icons-material/Insights';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import LandscapeIcon from '@mui/icons-material/Landscape';
import PlaceIcon from '@mui/icons-material/Place';
import SettingsIcon from '@mui/icons-material/Settings';
import clientService from 'src/clientService';
import {
  DeploymentDetailResponse,
  DeviceType,
  FileAdminDetailWithUser,
  FileDownloadResponse,
  HttpResponse,
  HTTPValidationError,
} from 'src/api/Api';
import { formatDistanceToNow } from 'date-fns';
import { TablePagination } from '@mui/material';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import UploadStatusDialog from './UploadStatusDialog';
import AudioPlayer from 'src/view/components/AudioPlayer';
import ImageOverlay from 'src/view/components/ImageOverlay';
import ConfirmationDialog from 'src/view/components/ConfirmationDialog';
import InferenceViewerDialog from 'src/view/Admin/components/InferenceViewerDialog';
import SoilChemistryDeploymentView from 'src/view/Admin/SoilChemistry/SoilChemistryDeploymentView';
import { CalendarMonth, DataObject, CheckCircle, Error } from '@mui/icons-material';
import { formatDate } from 'src/util/date-utils';
import { useSnackbar } from 'src/view/components/SnackbarService';

const AUDIO_EXTENSIONS = new Set(['.wav', '.wave', '.mp3', '.flac', '.ogg']);
const IMAGE_VIDEO_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif', '.mp4', '.avi', '.mov']);

function getFileExtension(filePath: string): string {
  const baseName = filePath.split(/[/\\]/).pop() ?? filePath;
  const dotIndex = baseName.lastIndexOf('.');
  if (dotIndex <= 0) return '';
  return baseName.slice(dotIndex).toLowerCase();
}

function isAudioDevice(deviceType: DeviceType | string): boolean {
  return String(deviceType).toUpperCase() === DeviceType.AUDIO;
}

function isValidFileForDevice(file: File, deviceType: DeviceType): boolean {
  const isAudio = isAudioDevice(deviceType);
  const extensions = isAudio ? AUDIO_EXTENSIONS : IMAGE_VIDEO_EXTENSIONS;
  const paths = [file.name, (file as File & { webkitRelativePath?: string }).webkitRelativePath].filter(
    Boolean,
  ) as string[];

  for (const path of paths) {
    const ext = getFileExtension(path);
    if (ext && extensions.has(ext)) return true;
  }

  if (isAudio && file.type.startsWith('audio/')) return true;
  if (!isAudio && (file.type.startsWith('image/') || file.type.startsWith('video/'))) {
    return true;
  }

  return false;
}

// Matches the UploadedFile shape UploadStatusDialog expects
interface UploadedFile {
  id: string;
  file: File;
  status: 'waiting' | 'uploading' | 'completed' | 'failed';
  error: string | null;
}

export enum FileStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  PROCESSING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

interface FileAdminDetailWithUserExtended extends FileAdminDetailWithUser {
  status: FileStatus;
}

type Order = 'asc' | 'desc';

interface Column {
  id:
    | keyof FileAdminDetailWithUserExtended
    | 'uploaded_by_user'
    | 'actions'
    | 'download'
    | 'inference'
    | 'spectrogram'
    | 'aci_indices'
    | 'inference_status';
  label: string;
  sortable?: boolean;
}

const columns: readonly Column[] = [
  { id: 'name', label: 'File Name', sortable: true },
  { id: 'uploaded_by_user', label: 'Uploaded By', sortable: false },
  { id: 'created_at', label: 'Uploaded At', sortable: true },
  { id: 'inference_status', label: 'Inference Status', sortable: false },
  { id: 'download', label: 'Download', sortable: false },
  { id: 'inference', label: 'Inference', sortable: false },
  { id: 'spectrogram', label: 'View Spectrogram', sortable: false },
  { id: 'aci_indices', label: 'Acoustic Indices', sortable: false },
  { id: 'actions', label: 'Actions', sortable: false },
];

function useDebouncedCallback<T extends (...args: any[]) => void>(fn: T, delay: number): T {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  return useCallback(
    ((...args: any[]) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => fn(...args), delay);
    }) as T,
    [fn, delay],
  );
}

// Default batch progress used before any upload starts
const INITIAL_BATCH_PROGRESS: BatchProgress = {
  phase: 'idle' as UploadPhase,
  uploadedCount: 0,
  totalCount: 0,
  failedCount: 0,
  overallPct: 0,
};

const Upload: FC = () => {
  const { deploymentId } = useParams<{ deploymentId: string }>();
  const [masterData, setMasterData] = useState<DeploymentDetailResponse | null>(null);
  const [files, setFiles] = useState<FileAdminDetailWithUserExtended[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filesLoading, setFilesLoading] = useState<boolean>(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // uploadQueue drives the file list in the status panel.
  // Each entry has a status ('waiting' | 'uploading' | 'completed' | 'failed').
  // The semaphore in useFileUpload controls which are 'uploading' at any time.
  const [uploadQueue, setUploadQueue] = useState<UploadedFile[]>([]);

  // batchProgress drives the aggregate progress bar and phase label.
  // It's updated by the onBatchProgress callback passed to uploadFiles().
  const [batchProgress, setBatchProgress] = useState<BatchProgress>(INITIAL_BATCH_PROGRESS);

  const [isUploadPanelOpen, setIsUploadPanelOpen] = useState(false);
  const [openInferenceViewerDialog, setOpenInferenceViewerDialog] = useState(false);
  const [currentInferenceData, setCurrentInferenceData] = useState<
    Record<string, any>[] | null | undefined
  >(undefined);
  const [selectedFile, setSelectedFile] = useState<FileAdminDetailWithUserExtended | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openMarkCompleteDialog, setOpenMarkCompleteDialog] = useState(false);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<
    keyof FileAdminDetailWithUserExtended | 'uploaded_by_user'
  >('created_at');
  const [inferencingFileIds, setInferencingFileIds] = useState<Set<string>>(new Set());
  const inferencingFileIdsRef = useRef<Set<string>>(new Set());
  const { showSnackbar, snackbarType } = useSnackbar();
  const [markingComplete, setMarkingComplete] = useState(false);
  const folderInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inferencingFileIdsRef.current = inferencingFileIds;
  }, [inferencingFileIds]);

  // Warn user before closing tab while an upload is in progress
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const isUploading = batchProgress.phase === 'uploading' || batchProgress.phase === 'preparing' || batchProgress.phase === 'finalizing';
      if (isUploading) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [batchProgress.phase]);

  const handleDismissUploadPanel = useCallback(() => {
    setUploadQueue([]);
    setBatchProgress(INITIAL_BATCH_PROGRESS);
    setIsUploadPanelOpen(false);
  }, []);

  const { uploadFiles } = useFileUpload();

  const acceptedFormats =
    masterData?.device.type === DeviceType.AUDIO
      ? '.wav, .mp3, .flac, .ogg'
      : '.jpg, .jpeg, .png, .gif, .mp4, .avi, .mov';

  const fetchFiles = useCallback(
    async (showLoading = false) => {
      if (!deploymentId) return;
      if (showLoading) setFilesLoading(true);
      setFilesError(null);

      try {
        const response =
          await clientService.api.getFilesForDeploymentApiV1AdminFilesDeploymentIdGet(deploymentId);

        setFiles((prevFiles) => {
          const serverFileIds = new Set(response.data.map((f) => f.file_id));

          const updatedFiles = response.data.map((file) => {
            let status: FileStatus;
            if (file.status) {
              const s = file.status.toUpperCase().trim();
              if (s === 'RUNNING' || s === 'PROCESSING') {
                status = FileStatus.RUNNING;
              } else if (s === 'COMPLETED') {
                status = FileStatus.COMPLETED;
                setInferencingFileIds((prev) => {
                  const next = new Set(prev);
                  next.delete(file.file_id);
                  return next;
                });
              } else if (s === 'FAILED') {
                status = FileStatus.FAILED;
                setInferencingFileIds((prev) => {
                  const next = new Set(prev);
                  next.delete(file.file_id);
                  return next;
                });
              } else {
                status = FileStatus.PENDING;
              }
            } else if (inferencingFileIdsRef.current.has(file.file_id)) {
              status = FileStatus.RUNNING;
            } else if (file.inferences && file.inferences.length > 0) {
              status = FileStatus.COMPLETED;
              setInferencingFileIds((prev) => {
                const next = new Set(prev);
                next.delete(file.file_id);
                return next;
              });
            } else {
              status = FileStatus.PENDING;
            }
            return { ...file, status } as FileAdminDetailWithUserExtended;
          });

          const localOnlyFiles = prevFiles.filter((f) => !serverFileIds.has(f.file_id));
          return [...updatedFiles, ...localOnlyFiles];
        });
      } catch (err) {
        setFilesError('Failed to fetch files.');
        console.error(err);
      } finally {
        if (showLoading) setFilesLoading(false);
      }
    },
    [deploymentId],
  );

  const debouncedFetchFiles = useDebouncedCallback(fetchFiles, 1000);

  // ---------------------------------------------------------------------------
  // Core upload handler — used by both dropzone and folder input.
  //
  // Key change from the old per-file approach:
  // - uploadQueue entries start as 'waiting' (not 'uploading').
  // - The semaphore inside useFileUpload controls when each file moves to
  //   'uploading'. We update the queue entry status when onBatchProgress
  //   reports phase='uploading' AND the file's bytes start moving.
  //   But since we don't get per-file activation callbacks from the semaphore,
  //   we rely on a simpler signal: the file's GCS PUT completion (result loop).
  //   During the upload phase, entries are marked 'uploading' in bulk when the
  //   phase starts — the panel shows the aggregate bar anyway, not per-file bars,
  //   so the exact per-file 'uploading' moment is cosmetic.
  // ---------------------------------------------------------------------------
  const handleFilesSelected = useCallback(
    async (acceptedFiles: File[]) => {
      if (!deploymentId) {
        setFilesError('Deployment ID is missing.');
        return;
      }
      if (acceptedFiles.length === 0) return;

      const existingFileNames = new Set(files.map((f) => f.name));

      // Build queue entries — duplicates are immediately 'failed'
      const queueEntries: UploadedFile[] = acceptedFiles.map((file) => ({
        id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
        file,
        status: existingFileNames.has(file.name) ? 'failed' : 'waiting',
        error: existingFileNames.has(file.name) ? 'Already uploaded' : null,
      }));

      setUploadQueue(queueEntries);
      setBatchProgress(INITIAL_BATCH_PROGRESS);
      setIsUploadPanelOpen(true);

      const toUpload = queueEntries.filter((e) => e.status !== 'failed');
      if (toUpload.length === 0) return;

      const items: UploadFileItem[] = toUpload.map((e) => ({ id: e.id, file: e.file }));

      // Mark non-duplicate entries as 'uploading' when the upload phase begins.
      // onBatchProgress fires for every chunk so we only flip status on first
      // 'uploading' phase transition to avoid churning state.
      let uploadingPhaseStarted = false;

      const results = await uploadFiles(
        items,
        deploymentId,
        (progress: BatchProgress) => {
          // Update aggregate progress bar in real time
          setBatchProgress(progress);

          // Flip all 'waiting' entries to 'uploading' on first upload phase tick
          if (progress.phase === 'uploading' && !uploadingPhaseStarted) {
            uploadingPhaseStarted = true;
            setUploadQueue((prev) =>
              prev.map((f) => (f.status === 'waiting' ? { ...f, status: 'uploading' } : f)),
            );
          }
        },
      );

      // Update per-file statuses from results after the batch completes
      const resultMap = new Map(results.map((r) => [r.id, r]));
      setUploadQueue((prev) =>
        prev.map((f) => {
          const result = resultMap.get(f.id);
          if (!result) return f; // duplicate entries not in results
          return {
            ...f,
            status: result.fileRecord ? 'completed' : 'failed',
            error: result.error ?? null,
          };
        }),
      );

      // Add successfully finalized files to the table
      for (const result of results) {
        if (result.fileRecord) {
          const backendStatus = result.fileRecord.status?.toUpperCase();
          const initialStatus: FileStatus =
            backendStatus === 'RUNNING' || backendStatus === 'PROCESSING'
              ? FileStatus.RUNNING
              : backendStatus === 'COMPLETED'
              ? FileStatus.COMPLETED
              : backendStatus === 'FAILED'
              ? FileStatus.FAILED
              : FileStatus.PENDING;

          const newFile: FileAdminDetailWithUserExtended = {
            ...result.fileRecord,
            status: initialStatus,
          };

          setFiles((prevFiles) => {
            const existingIndex = prevFiles.findIndex(
              (f) => f.file_id === result.fileRecord!.file_id,
            );
            if (existingIndex >= 0) {
              const updated = [...prevFiles];
              updated[existingIndex] = newFile;
              return updated;
            }
            return [newFile, ...prevFiles];
          });
        }
      }

      debouncedFetchFiles(false);
    },
    [deploymentId, files, uploadFiles, debouncedFetchFiles],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => { handleFilesSelected(acceptedFiles); },
    [handleFilesSelected],
  );

  const onFolderSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList || fileList.length === 0) return;

      if (!masterData) return;

      const deviceType = masterData.device.type;
      const filtered = Array.from(fileList).filter((f) => isValidFileForDevice(f, deviceType));

      if (filtered.length === 0) {
        showSnackbar({
          message: `No valid ${isAudioDevice(deviceType) ? 'audio' : 'image/video'} files found in the selected folder.`,
          type: snackbarType.WARNING,
        });
        e.target.value = '';
        return;
      }

      e.target.value = '';
      handleFilesSelected(filtered);
    },
    [masterData, handleFilesSelected, showSnackbar, snackbarType],
  );

  const hasPendingOrRunning = useMemo(
    () =>
      files.some(
        (f) =>
          f.status === FileStatus.PENDING ||
          f.status === FileStatus.RUNNING ||
          inferencingFileIds.has(f.file_id),
      ),
    [files, inferencingFileIds],
  );

  useEffect(() => {
    if (!hasPendingOrRunning) return;
    const interval = setInterval(() => { fetchFiles(false); }, 2000);
    return () => clearInterval(interval);
  }, [hasPendingOrRunning, fetchFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  useEffect(() => {
    const fetchDeploymentDetails = async () => {
      if (!deploymentId) return;
      setLoading(true);
      try {
        const response =
          await clientService.api.getDeploymentDetailsAdminApiV1AdminDeploymentsDeploymentIdGet(
            deploymentId,
          );
        setMasterData(response.data);
      } catch (err) {
        setError('Failed to fetch deployment details.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDeploymentDetails();
    fetchFiles(true);
  }, [deploymentId, fetchFiles]);

  const handleViewInference = (file: FileAdminDetailWithUserExtended) => {
    const data = file?.inferences?.map((f) => f?.data) as Record<string, any>[] | null | undefined;
    setCurrentInferenceData(data);
    setOpenInferenceViewerDialog(true);
  };

  const handleViewAcousticIndices = (file: FileAdminDetailWithUserExtended) => {
    const data = file?.inferences?.map((f) => f?.acoustic_indices) as Record<string, any>[] | null | undefined;
    setCurrentInferenceData(data);
    setOpenInferenceViewerDialog(true);
  };

  const handleDeleteFile = async () => {
    if (selectedFile) {
      try {
        await clientService.api.deleteApiFileApiV1AdminFilesFileIdDelete(selectedFile.file_id);
        setFiles((prevFiles) => prevFiles.filter((file) => file.file_id !== selectedFile.file_id));
      } catch {
        showSnackbar({ message: 'Failed to delete file', type: snackbarType.ERROR });
      }
    }
    setOpenConfirmDialog(false);
  };

  const handleTriggerInference = async (file: FileAdminDetailWithUserExtended) => {
    setInferencingFileIds((prev) => { const next = new Set(prev); next.add(file.file_id); return next; });
    setFiles((prevFiles) =>
      prevFiles.map((f) => f.file_id === file.file_id ? { ...f, status: FileStatus.RUNNING } : f),
    );
    try {
      await clientService.api.runInferenceOnFileApiV1AdminInferenceFileFileIdPost(file.file_id);
      await fetchFiles(false);
    } catch {
      showSnackbar({ message: 'Failed to trigger inference', type: snackbarType.ERROR });
      setFiles((prevFiles) =>
        prevFiles.map((f) => f.file_id === file.file_id ? { ...f, status: FileStatus.FAILED } : f),
      );
      setInferencingFileIds((prev) => { const next = new Set(prev); next.delete(file.file_id); return next; });
    }
  };

  const handleMarkComplete = async () => {
    if (!deploymentId) return;
    setMarkingComplete(true);
    try {
      await clientService.api.updateApiDeploymentApiV1AdminDeploymentsDeploymentIdPut(
        deploymentId,
        { data_upload_status: true },
      );
      setMasterData((prev) => {
        if (!prev) return null;
        return { ...prev, deployment: { ...prev.deployment, data_upload_status: true } };
      });
    } catch {
      showSnackbar({ message: 'Failed to mark as complete', type: snackbarType.ERROR });
    } finally {
      setMarkingComplete(false);
      setOpenMarkCompleteDialog(false);
    }
  };

  const handleRequestSort = (property: keyof FileAdminDetailWithUserExtended | 'uploaded_by_user') => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as keyof FileAdminDetailWithUserExtended | 'uploaded_by_user');
  };

  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      const aValue =
        orderBy === 'uploaded_by_user' ? a.uploaded_by_user.first_name : a[orderBy as keyof FileAdminDetailWithUserExtended];
      const bValue =
        orderBy === 'uploaded_by_user' ? b.uploaded_by_user.first_name : b[orderBy as keyof FileAdminDetailWithUserExtended];
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [files, order, orderBy]);

  const paginatedFiles = sortedFiles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!masterData) return <Typography variant="h6">Deployment not found.</Typography>;

  if (
    (masterData.device.type as string) === 'SOIL_SENSOR' ||
    (masterData.device.type as string) === 'SOIL_CHEMISTRY'
  ) {
    return <SoilChemistryDeploymentView masterData={masterData} />;
  }

  const { project, site, location, deployment, device } = masterData;

  return (
    <Box sx={{ p: 3 }}>
      {/* Project Details Header */}
      <Paper elevation={3} sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h1" fontWeight="bold">
            Project Details
          </Typography>
          {!deployment.data_upload_status && (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => setOpenMarkCompleteDialog(true)}
              disabled={markingComplete}
            >
              {markingComplete ? 'Marking...' : 'Mark Complete'}
            </Button>
          )}
        </Box>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessCenterIcon sx={{ mr: 1 }} />
              <Typography variant="body1" component="div">
                <strong>Project:</strong>{' '}
                <Link to={`/admin/project/${project.project_id}`} style={{ color: 'secondary' }}>
                  {project.project_name}
                </Link>
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LandscapeIcon sx={{ mr: 1 }} />
              <Typography variant="body1" component="div">
                <strong>Site:</strong>{' '}
                <Link to={`/admin/project/${project.project_id}/site/${site.site_id}`} style={{ color: 'secondary' }}>
                  {site.site_name}
                </Link>
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PlaceIcon sx={{ mr: 1 }} />
              <Typography variant="body1">
                <strong>Location:</strong> {location.location_name}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 1 }} />
              <Typography variant="body1">
                <strong>Deployment:</strong> {deployment.deployment_name} (Device:{' '}
                {device.human_readable_id} - {device.type})
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarMonth sx={{ mr: 1 }} />
              <Typography variant="body1">
                <strong>Period:</strong> {formatDate(deployment.start_date)} -{' '}
                {formatDate(deployment.end_date)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Upload Zone */}
      {deployment.data_upload_status ? (
        <Alert severity="info">
          This deployment has been marked as complete. No further uploads are possible. If you need
          to upload additional data, please contact administrative support for assistance.
        </Alert>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 4,
              minHeight: 180,
              border: '2px dashed #d0d0d0',
              backgroundColor: '#fefefe',
              borderRadius: 3,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              transition: '0.25s ease',
              cursor: 'pointer',
              '&:hover': { borderColor: '#9c27b0', backgroundColor: '#faf5fc' },
            }}
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontSize: '1.05rem' }}>
              {isDragActive
                ? 'Drop the files here...'
                : 'Drag & drop files here, or click to select files.'}
            </Typography>
            <Alert severity="info" sx={{ width: '100%', maxWidth: 450 }}>
              Please upload files relevant to a <strong>{device.type}</strong> device. Accepted
              formats: {acceptedFormats}.
            </Alert>
          </Paper>

          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <input
              ref={folderInputRef}
              type="file"
              // @ts-ignore — non-standard attributes for folder selection
              webkitdirectory=""
              directory=""
              multiple
              style={{ display: 'none' }}
              onChange={onFolderSelected}
            />
            <Button
              variant="outlined"
              startIcon={<FolderOpenIcon />}
              onClick={() => folderInputRef.current?.click()}
            >
              Upload Folder
            </Button>
          </Box>
        </>
      )}

      {/* Upload status panel */}
      {uploadQueue.length > 0 && (
        <UploadStatusDialog
          open={isUploadPanelOpen}
          onClose={() => setIsUploadPanelOpen(false)}
          onDismiss={handleDismissUploadPanel}
          uploadQueue={uploadQueue}
          batchProgress={batchProgress}
        />
      )}

      {/* Files Table */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Uploaded Files
      </Typography>
      {filesLoading ? (
        <CircularProgress />
      ) : filesError ? (
        <Alert severity="error">{filesError}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => {
                  if (deployment.data_upload_status && column.id === 'actions') return null;
                  return (
                    <TableCell key={column.id} align="justify">
                      {column.sortable ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : 'asc'}
                          onClick={() =>
                            handleRequestSort(column.id as keyof FileAdminDetailWithUserExtended | 'uploaded_by_user')
                          }
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedFiles.map((file) => (
                <TableRow key={file.file_id}>
                  <TableCell>{file.name}</TableCell>
                  <TableCell>{file.uploaded_by_user.first_name}</TableCell>
                  <TableCell>
                    {formatDistanceToNow(new Date(file.created_at + 'Z'), { addSuffix: true })}
                  </TableCell>
                  <TableCell>
                    {file.status === FileStatus.RUNNING || inferencingFileIds.has(file.file_id) ? (
                      <Chip
                        label="Processing"
                        color="warning"
                        size="small"
                        icon={<CircularProgress size={16} sx={{ color: 'inherit' }} />}
                      />
                    ) : file.status === FileStatus.COMPLETED ? (
                      <Chip
                        label="Completed"
                        color="success"
                        size="small"
                        icon={<CheckCircle sx={{ color: 'inherit', fontSize: '16px !important' }} />}
                      />
                    ) : file.status === FileStatus.FAILED ? (
                      <Chip
                        label="Failed"
                        color="error"
                        size="small"
                        icon={<Error sx={{ color: 'inherit', fontSize: '16px !important' }} />}
                      />
                    ) : (
                      <Chip label="Pending" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {masterData.device.type === DeviceType.AUDIO ? (
                      <AudioPlayer fileId={file.file_id} />
                    ) : masterData.device.type === DeviceType.CAMERA ? (
                      <ImageOverlay
                        alt={file.name}
                        getFileUrl={() =>
                          clientService.api
                            .getDownloadUrlApiV1AdminFilesFileIdDownloadUrlGet(file.file_id)
                            .then((res: HttpResponse<FileDownloadResponse, HTTPValidationError>) => res.data.download_url)
                        }
                      />
                    ) : (
                      <IconButton component="a" href={file.url} target="_blank" rel="noopener noreferrer" download>
                        <DownloadIcon />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell>
                    {file.inferences && file.inferences.length > 0 && (
                      <IconButton onClick={() => handleViewInference(file)}>
                        <DataObject />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell sx={{ pl: 6 }}>
                    {file.inferences && file.inferences.length > 0 && (
                      <ImageOverlay
                        alt={file.name}
                        getFileUrl={() =>
                          clientService.api
                            .getSpectrogramUrlApiV1AdminFilesFileIdSpectrogramUrlGet(file.file_id)
                            .then((res: HttpResponse<FileDownloadResponse, HTTPValidationError>) => res.data.download_url)
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ pl: 6 }}>
                    {file.inferences && file.inferences.length > 0 && (
                      <IconButton onClick={() => handleViewAcousticIndices(file)}>
                        <DataObject />
                      </IconButton>
                    )}
                  </TableCell>
                  {!deployment.data_upload_status && (
                    <TableCell align="left">
                      <IconButton onClick={() => { setSelectedFile(file); setOpenConfirmDialog(true); }}>
                        <DeleteIcon />
                      </IconButton>
                      {(!file.inferences || file.inferences.length === 0) &&
                        !inferencingFileIds.has(file.file_id) &&
                        file.status !== FileStatus.RUNNING &&
                        file.status !== FileStatus.PENDING &&
                        file.status !== FileStatus.COMPLETED && (
                          <IconButton onClick={() => handleTriggerInference(file)}>
                            <InsightsIcon />
                          </IconButton>
                        )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100, { label: 'All', value: -1 }]}
            component="div"
            count={files.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      <ConfirmationDialog
        open={openConfirmDialog}
        onCancel={() => setOpenConfirmDialog(false)}
        onConfirm={handleDeleteFile}
        title="Delete File"
        content="Are you sure you want to delete this file?"
      />
      <ConfirmationDialog
        open={openMarkCompleteDialog}
        onCancel={() => setOpenMarkCompleteDialog(false)}
        onConfirm={handleMarkComplete}
        title="Mark As Complete"
        content="Are you sure you want to mark this deployment as complete? Once completed, you will not be able to upload any data for this deployment."
        confirmButtonText="Yes, Mark as Complete"
      />
      <InferenceViewerDialog
        open={openInferenceViewerDialog}
        onClose={() => setOpenInferenceViewerDialog(false)}
        inferenceData={currentInferenceData}
      />
    </Box>
  );
};

export default Upload;